import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Alert, Spinner, Badge, Button, Form, Row, Col } from 'react-bootstrap';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const formatTimestamp = (value) => {
  if (!value) return 'N/A';
  if (value.toDate) {
    const date = value.toDate();
    if (Number.isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    const date = new Date(value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6));
    if (Number.isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SessionPollAdmin = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [votes, setVotes] = useState([]);
  const [eligibleLearners, setEligibleLearners] = useState([]);
  const [allLearners, setAllLearners] = useState([]);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [polls, setPolls] = useState([]);
  const [selectedPollId, setSelectedPollId] = useState('');
  const [pollOptions, setPollOptions] = useState([]);
  const [pollTitle, setPollTitle] = useState('');
  const [newPollTitle, setNewPollTitle] = useState('');
  const [newPollOptions, setNewPollOptions] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [reminderDeadlineDate, setReminderDeadlineDate] = useState('');
  const [reminderDeadlineTime, setReminderDeadlineTime] = useState('');
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderResult, setReminderResult] = useState('');
  const [optionTargetCohorts, setOptionTargetCohorts] = useState({});
  const [optionMoveSelections, setOptionMoveSelections] = useState({});
  const [movingOptionId, setMovingOptionId] = useState('');
  const [moveNonVotersCohortId, setMoveNonVotersCohortId] = useState('');
  const [moveStatus, setMoveStatus] = useState('');
  const [movingNonVoters, setMovingNonVoters] = useState(false);

  useEffect(() => {
    const loadPoll = async () => {
      try {
        setLoading(true);
        setError('');

        try {
          const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
          const cohortList = cohortsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCohorts(cohortList);
          const cohortNameMap = cohortList.reduce((acc, cohort) => {
            acc[cohort.id] = cohort.name || cohort.cohortName || cohort.id;
            return acc;
          }, {});

          const learners = [];
          for (let i = 0; i < cohortList.length; i += 10) {
            const chunk = cohortList.slice(i, i + 10).map(cohort => cohort.id);
            if (chunk.length === 0) continue;
            const learnersQuery = query(
              collection(db, 'users'),
              where('role', '==', 'learner'),
              where('cohortId', 'in', chunk)
            );
            const learnersSnapshot = await getDocs(learnersQuery);
            learnersSnapshot.docs.forEach(doc => {
              const data = doc.data();
              learners.push({
                id: doc.id,
                email: data.email || '',
                name: data.displayName || data.name || data.email || 'N/A',
                cohortId: data.cohortId || '',
                cohortName: cohortNameMap[data.cohortId] || data.cohortName || data.cohortId || '',
                pollMoveHistory: Array.isArray(data.pollMoveHistory) ? data.pollMoveHistory : []
              });
            });
          }
          setAllLearners(learners);
        } catch (cohortError) {
          devError('Error loading eligible learners:', cohortError);
          setWarning('Poll responses loaded, but learner eligibility could not be verified.');
        }

        try {
          const pollsSnapshot = await getDocs(
            query(collection(db, 'events'), where('type', '==', 'session_poll'))
          );
          const pollList = pollsSnapshot.docs
            .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
            .sort((a, b) => {
              const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
              const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
              return bTime - aTime;
            });
          setPolls(pollList);
          if (!selectedPollId && pollList.length > 0) {
            setSelectedPollId(pollList[0].id);
            setPollOptions(pollList[0].options || []);
            setPollTitle(pollList[0].title || 'Session Poll');
            const allowedIds = pollList[0].allowedCohortIds || [];
            setEligibleLearners(
              allowedIds.length
                ? allLearners.filter(learner => allowedIds.includes(learner.cohortId))
                : allLearners
            );
          }
        } catch (pollError) {
          devError('Error loading polls:', pollError);
          setError('Could not load poll configuration.');
        }
      } catch (err) {
        devError('Error loading poll responses:', err);
        setError('Could not load poll responses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadPoll();
  }, [selectedPollId]);

  useEffect(() => {
    if (!selectedPollId) {
      setEligibleLearners(allLearners);
      return;
    }
    const selectedPoll = polls.find(poll => poll.id === selectedPollId);
    const allowedIds = selectedPoll?.allowedCohortIds || [];
    setEligibleLearners(
      allowedIds.length ? allLearners.filter(learner => allowedIds.includes(learner.cohortId)) : allLearners
    );
  }, [selectedPollId, polls, allLearners]);

  useEffect(() => {
    const loadResponses = async () => {
      if (!selectedPollId) {
        setVotes([]);
        return;
      }
      try {
        const functions = getFunctions();
        const getPollResponses = httpsCallable(functions, 'getSessionPollResponses');
        const response = await getPollResponses({ eventId: selectedPollId });
        setVotes(response.data.responses || []);
      } catch (pollError) {
        devError('Callable poll response failed, falling back:', pollError);
        const pollRef = collection(db, 'events', selectedPollId, 'polls');
        const pollQuery = query(pollRef, orderBy('votedAt', 'desc'));
        const snapshot = await getDocs(pollQuery);
        const records = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        setVotes(records);
      }
    };

    loadResponses();
  }, [selectedPollId]);

  const toggleCohort = (cohortId) => {
    setSelectedCohortIds(prev => (
      prev.includes(cohortId) ? prev.filter(id => id !== cohortId) : [...prev, cohortId]
    ));
  };

  const createPoll = async () => {
    if (!newPollTitle.trim()) {
      setError('Please enter a poll title.');
      return;
    }
    const options = newPollOptions
      .split('\n')
      .map(option => option.trim())
      .filter(Boolean)
      .map((label, index) => ({ id: `option_${index + 1}`, label }));
    if (!options.length) {
      setError('Please add at least one option.');
      return;
    }

    try {
      setCreatingPoll(true);
      setError('');
      const activePolls = await getDocs(
        query(collection(db, 'events'), where('type', '==', 'session_poll'), where('status', '==', 'active'))
      );
      const updates = activePolls.docs.map(docSnap =>
        updateDoc(doc(db, 'events', docSnap.id), { status: 'closed', closedAt: serverTimestamp() })
      );
      await Promise.all(updates);

      const cohortNames = selectedCohortIds
        .map(id => cohorts.find(c => c.id === id)?.name || '')
        .filter(Boolean);

      const pollDoc = await addDoc(collection(db, 'events'), {
        type: 'session_poll',
        title: newPollTitle.trim(),
        options,
        allowedCohortIds: selectedCohortIds,
        allowedCohortNames: cohortNames,
        status: 'active',
        createdAt: serverTimestamp()
      });

      setSelectedPollId(pollDoc.id);
      setPollOptions(options);
      setPollTitle(newPollTitle.trim());
      setNewPollTitle('');
      setNewPollOptions('');
      setSelectedCohortIds([]);
    } catch (err) {
      devError('Error creating poll:', err);
      setError('Could not create poll.');
    } finally {
      setCreatingPoll(false);
    }
  };

  const optionCounts = useMemo(() => {
    return (pollOptions || []).reduce((acc, option) => {
      acc[option.id] = votes.filter(vote => vote.optionId === option.id).length;
      return acc;
    }, {});
  }, [votes, pollOptions]);

  const nonVoters = useMemo(() => {
    const voterIds = new Set(votes.map(vote => vote.studentId || vote.id));
    return eligibleLearners.filter(learner => !voterIds.has(learner.id));
  }, [eligibleLearners, votes]);

  const canManageCohorts = userProfile?.role === 'admin';

  const hasMovedForOption = (learner, optionId) => {
    if (!learner?.pollMoveHistory || !selectedPollId) return false;
    return learner.pollMoveHistory.some(entry =>
      entry?.eventId === selectedPollId && entry?.optionId === optionId
    );
  };

  const votersByOption = useMemo(() => {
    const map = {};
    const learnerMap = new Map(eligibleLearners.map(learner => [learner.id, learner]));
    pollOptions.forEach((option) => {
      map[option.id] = votes
        .filter(vote => vote.optionId === option.id)
        .map(vote => {
          const learner = learnerMap.get(vote.studentId || vote.id) || {};
          return {
            id: vote.studentId || vote.id,
            name: learner.name || vote.studentName || 'N/A',
            email: learner.email || vote.studentEmail || 'N/A',
            cohortId: learner.cohortId || vote.cohortId || null,
            moved: hasMovedForOption(learner, option.id)
          };
        })
        .filter(vote => vote.id);
    });
    return map;
  }, [votes, eligibleLearners, pollOptions, selectedPollId]);

  const toggleMoveSelection = (optionId, learnerId) => {
    setOptionMoveSelections(prev => {
      const current = new Set(prev[optionId] || []);
      if (current.has(learnerId)) {
        current.delete(learnerId);
      } else {
        current.add(learnerId);
      }
      return { ...prev, [optionId]: Array.from(current) };
    });
  };

  const selectAllNotMoved = (optionId) => {
    const voters = votersByOption[optionId] || [];
    const selectable = voters.filter(voter => !voter.moved).map(voter => voter.id);
    setOptionMoveSelections(prev => ({ ...prev, [optionId]: selectable }));
  };

  const buildReminderEmail = () => {
    const deadlineLabel = reminderDeadlineDate
      ? `${reminderDeadlineDate}${reminderDeadlineTime ? ` ${reminderDeadlineTime}` : ''}`
      : 'today';
    const dashboardUrl = `${window.location.origin}/dashboard`;
    const subject = `Reminder: ${pollTitle || 'Session Poll'} - Vote by ${deadlineLabel}`;
    const html = `
      <p>Hello {name},</p>
      <p>This is a friendly reminder to vote in the <strong>${pollTitle || 'session poll'}</strong>.</p>
      <p><strong>Deadline:</strong> ${deadlineLabel}</p>
      <p>Please open your dashboard to vote: <a href="${dashboardUrl}">${dashboardUrl}</a></p>
      <p>Thank you!</p>
    `;
    const text = `Hello {name},\n\nThis is a reminder to vote in the ${pollTitle || 'session poll'}.\nDeadline: ${deadlineLabel}\nVote here: ${dashboardUrl}\n\nThank you!`;
    return { subject, html, text };
  };

  const sendReminderEmails = async () => {
    if (nonVoters.length === 0) {
      setReminderResult('Everyone has already voted.');
      return;
    }

    const recipients = nonVoters
      .map(learner => learner.email)
      .filter(email => typeof email === 'string' && email.includes('@'));

    if (recipients.length === 0) {
      setReminderResult('No valid email addresses found for non-voters.');
      return;
    }

    try {
      setReminderSending(true);
      setReminderResult('');
      const { subject, html, text } = buildReminderEmail();
      const functions = getFunctions();
      const sendCustomEmail = httpsCallable(functions, 'sendCustomEmail');
      const response = await sendCustomEmail({
        subject,
        htmlContent: html,
        textContent: text,
        recipientType: 'specific',
        recipients
      });
      const message = response?.data?.message || `Reminder emails sent to ${recipients.length} learners.`;
      setReminderResult(message);
    } catch (reminderError) {
      devError('Error sending reminder emails:', reminderError);
      setReminderResult(reminderError?.message || 'Failed to send reminder emails.');
    } finally {
      setReminderSending(false);
    }
  };

  const updateLearnersToCohort = async (learnerList, targetCohortId, reasonLabel, moveMeta = null) => {
    if (!targetCohortId) {
      setMoveStatus('Select a target cohort first.');
      return { success: 0, failed: 0 };
    }
    const targetCohort = cohorts.find(c => c.id === targetCohortId);
    const targetProgramId = targetCohort?.programId || null;
    let successCount = 0;
    let failedCount = 0;
    const movedAt = serverTimestamp();

    for (const learner of learnerList) {
      try {
        const moveHistoryEntry = moveMeta
          ? {
              eventId: moveMeta.eventId,
              pollTitle: moveMeta.pollTitle,
              optionId: moveMeta.optionId,
              optionLabel: moveMeta.optionLabel,
              movedToCohortId: targetCohortId,
              movedAt,
              movedBy: currentUser?.uid || null
            }
          : null;
        await updateDoc(doc(db, 'users', learner.id), {
          cohortId: targetCohortId,
          programId: targetProgramId || learner.programId || null,
          updatedAt: serverTimestamp(),
          transferHistory: arrayUnion({
            fromCohortId: learner.cohortId || null,
            toCohortId: targetCohortId,
            reason: reasonLabel,
            date: new Date().toISOString(),
            transferredBy: currentUser?.uid || null
          }),
          ...(moveHistoryEntry ? { pollMoveHistory: arrayUnion(moveHistoryEntry) } : {})
        });
        successCount += 1;
      } catch (moveError) {
        devError('Failed to move learner', learner.id, moveError);
        failedCount += 1;
      }
    }

    return { success: successCount, failed: failedCount };
  };

  const moveLearnersByOption = async (optionId) => {
    if (!canManageCohorts) {
      setMoveStatus('Only admins can move learners between cohorts.');
      return;
    }
    const targetCohortId = optionTargetCohorts[optionId] || '';
    if (!targetCohortId) {
      setMoveStatus('Select a target cohort for this poll option.');
      return;
    }
    const selectedIds = optionMoveSelections[optionId] || [];
    if (selectedIds.length === 0) {
      setMoveStatus('Select learners to move for this option.');
      return;
    }
    const votersForOption = (votersByOption[optionId] || [])
      .filter(voter => selectedIds.includes(voter.id))
      .map(voter => ({
        id: voter.id,
        cohortId: voter.cohortId || null,
        programId: null
      }))
      .filter(voter => voter.id);

    if (votersForOption.length === 0) {
      setMoveStatus('No selectable learners found for this option.');
      return;
    }

    setMovingOptionId(optionId);
    setMoveStatus('');
    const optionLabel = pollOptions.find(o => o.id === optionId)?.label || optionId;
    const results = await updateLearnersToCohort(
      votersForOption,
      targetCohortId,
      `Poll selection (${pollTitle || 'Session Poll'}): ${optionLabel}`,
      {
        eventId: selectedPollId,
        optionId,
        optionLabel,
        pollTitle: pollTitle || 'Session Poll'
      }
    );
    setMoveStatus(`Moved ${results.success} learners. ${results.failed ? `${results.failed} failed.` : ''}`);
    if (results.success > 0) {
      const movedAt = new Date().toISOString();
      setAllLearners(prev => prev.map((learner) => {
        if (!selectedIds.includes(learner.id)) return learner;
        const history = Array.isArray(learner.pollMoveHistory) ? learner.pollMoveHistory : [];
        return {
          ...learner,
          pollMoveHistory: [
            ...history,
            {
              eventId: selectedPollId,
              pollTitle: pollTitle || 'Session Poll',
              optionId,
              optionLabel,
              movedToCohortId: targetCohortId,
              movedAt
            }
          ]
        };
      }));
    }
    setOptionMoveSelections(prev => ({ ...prev, [optionId]: [] }));
    setMovingOptionId('');
  };

  const moveNonVoters = async () => {
    if (!canManageCohorts) {
      setMoveStatus('Only admins can move learners between cohorts.');
      return;
    }
    if (nonVoters.length === 0) {
      setMoveStatus('No non-voters to move.');
      return;
    }
    setMovingNonVoters(true);
    setMoveStatus('');
    const results = await updateLearnersToCohort(
      nonVoters,
      moveNonVotersCohortId,
      `Poll non-voter transfer (${pollTitle || 'Session Poll'})`,
      {
        eventId: selectedPollId,
        optionId: 'non-voter',
        optionLabel: 'Non-voter',
        pollTitle: pollTitle || 'Session Poll'
      }
    );
    setMoveStatus(`Moved ${results.success} non-voters. ${results.failed ? `${results.failed} failed.` : ''}`);
    if (results.success > 0) {
      const movedAt = new Date().toISOString();
      const movedIds = new Set(nonVoters.map((learner) => learner.id));
      setAllLearners(prev => prev.map((learner) => {
        if (!movedIds.has(learner.id)) return learner;
        const history = Array.isArray(learner.pollMoveHistory) ? learner.pollMoveHistory : [];
        return {
          ...learner,
          pollMoveHistory: [
            ...history,
            {
              eventId: selectedPollId,
              pollTitle: pollTitle || 'Session Poll',
              optionId: 'non-voter',
              optionLabel: 'Non-voter',
              movedToCohortId: moveNonVotersCohortId,
              movedAt
            }
          ]
        };
      }));
    }
    setMovingNonVoters(false);
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-1">{pollTitle || 'Session Poll'}</h6>
          <small className="text-muted">Event ID: {selectedPollId || 'Not selected'}</small>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {(pollOptions || []).map(option => (
            <Badge key={option.id} bg="secondary">
              {option.label}: {optionCounts[option.id] || 0}
            </Badge>
          ))}
          <Badge bg="info">
            Eligible learners: {eligibleLearners.length}
          </Badge>
          <Badge bg="warning" text="dark">
            Not voted: {nonVoters.length}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Create New Poll</h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Poll Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={newPollTitle}
                    onChange={(e) => setNewPollTitle(e.target.value)}
                    placeholder="e.g. Session Time Poll"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Poll Options (one per line)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newPollOptions}
                    onChange={(e) => setNewPollOptions(e.target.value)}
                    placeholder="Morning session\nAfternoon session\nEvening session"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Label>Eligible Cohorts</Form.Label>
                <div className="border rounded p-2" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                  {cohorts.map(cohort => (
                    <Form.Check
                      key={cohort.id}
                      type="checkbox"
                      id={`poll-cohort-${cohort.id}`}
                      label={cohort.name || cohort.cohortName || cohort.id}
                      checked={selectedCohortIds.includes(cohort.id)}
                      onChange={() => toggleCohort(cohort.id)}
                    />
                  ))}
                </div>
              </Col>
            </Row>
            <Button variant="primary" onClick={createPoll} disabled={creatingPoll}>
              {creatingPoll ? 'Creating...' : 'Create Poll'}
            </Button>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Reminder Email to Non-Voters</h6>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Deadline Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={reminderDeadlineDate}
                    onChange={(e) => setReminderDeadlineDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Deadline Time (optional)</Form.Label>
                  <Form.Control
                    type="time"
                    value={reminderDeadlineTime}
                    onChange={(e) => setReminderDeadlineTime(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button variant="warning" onClick={sendReminderEmails} disabled={reminderSending || nonVoters.length === 0}>
                  {reminderSending ? 'Sending...' : `Email ${nonVoters.length} Non-Voters`}
                </Button>
              </Col>
            </Row>
            {reminderResult && <Alert variant="info" className="mb-0">{reminderResult}</Alert>}
          </Card.Body>
        </Card>

        <Form.Group className="mb-3">
          <Form.Label>View Poll</Form.Label>
            <Form.Select
              value={selectedPollId}
              onChange={(e) => {
                const pollId = e.target.value;
                setSelectedPollId(pollId);
                const poll = polls.find(item => item.id === pollId);
                setPollOptions(poll?.options || []);
                setPollTitle(poll?.title || 'Session Poll');
                const allowedIds = poll?.allowedCohortIds || [];
                setEligibleLearners(
                  allowedIds.length ? allLearners.filter(learner => allowedIds.includes(learner.cohortId)) : allLearners
                );
              }}
            >
            <option value="">Select a poll...</option>
            {polls.map(poll => (
              <option key={poll.id} value={poll.id}>
                {poll.title || poll.id}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {warning && <Alert variant="warning">{warning}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading poll responses...</p>
          </div>
        ) : (
          <>
            {votes.length === 0 ? (
              <Alert variant="info">
                No poll responses submitted yet.
              </Alert>
            ) : (
              <>
                <h6>Responses</h6>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Learner</th>
                      <th>Email</th>
                      <th>Selected Option</th>
                      <th>Voted At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {votes.map((vote) => (
                      <tr key={vote.id}>
                        <td>{vote.studentName || 'N/A'}</td>
                        <td>{vote.studentEmail || 'N/A'}</td>
                        <td>{vote.optionLabel || 'N/A'}</td>
                        <td>{formatTimestamp(vote.votedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            <hr />

            <h6>Not Yet Voted</h6>
            {nonVoters.length === 0 ? (
              <Alert variant="success" className="mb-0">
                Everyone in the eligible cohorts has voted.
              </Alert>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Email</th>
                    <th>Cohort</th>
                  </tr>
                </thead>
                <tbody>
                  {nonVoters.map((learner) => (
                    <tr key={learner.id}>
                      <td>{learner.name}</td>
                      <td>{learner.email || 'N/A'}</td>
                      <td>{learner.cohortName || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <hr />

            <h6>Move Learners by Poll Option</h6>
            {!canManageCohorts ? (
              <Alert variant="warning">Only admins can move learners between cohorts.</Alert>
            ) : (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Poll Option</th>
                    <th>Target Cohort</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pollOptions.map((option) => (
                    <tr key={option.id}>
                      <td>{option.label}</td>
                      <td>
                        <Form.Select
                          value={optionTargetCohorts[option.id] || ''}
                          onChange={(e) =>
                            setOptionTargetCohorts((prev) => ({
                              ...prev,
                              [option.id]: e.target.value
                            }))
                          }
                        >
                          <option value="">Select cohort...</option>
                          {cohorts.map((cohort) => (
                            <option key={cohort.id} value={cohort.id}>
                              {cohort.name || cohort.cohortName || cohort.id}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          onClick={() => moveLearnersByOption(option.id)}
                          disabled={movingOptionId === option.id}
                        >
                          {movingOptionId === option.id ? 'Moving...' : 'Move Learners'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {pollOptions.map((option) => {
              const voters = votersByOption[option.id] || [];
              if (voters.length === 0) {
                return null;
              }
              return (
                <Card key={`option-move-${option.id}`} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>{option.label}</strong>
                    <Button variant="outline-secondary" size="sm" onClick={() => selectAllNotMoved(option.id)}>
                      Select All Not Moved
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table bordered hover size="sm">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Learner</th>
                            <th>Email</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {voters.map((voter) => (
                            <tr key={voter.id}>
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  checked={(optionMoveSelections[option.id] || []).includes(voter.id)}
                                  disabled={voter.moved}
                                  onChange={() => toggleMoveSelection(option.id, voter.id)}
                                />
                              </td>
                              <td>{voter.name}</td>
                              <td>{voter.email}</td>
                              <td>
                                {voter.moved ? (
                                  <Badge bg="success">Moved</Badge>
                                ) : (
                                  <Badge bg="secondary">Not moved</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}

            <h6>Move Non-Voters (Failsafe)</h6>
            {!canManageCohorts ? (
              <Alert variant="warning">Only admins can move learners between cohorts.</Alert>
            ) : (
              <Row className="align-items-end">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Target Cohort</Form.Label>
                    <Form.Select
                      value={moveNonVotersCohortId}
                      onChange={(e) => setMoveNonVotersCohortId(e.target.value)}
                    >
                      <option value="">Select cohort...</option>
                      {cohorts.map((cohort) => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.name || cohort.cohortName || cohort.id}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Button
                    variant="outline-danger"
                    onClick={moveNonVoters}
                    disabled={movingNonVoters || nonVoters.length === 0}
                  >
                    {movingNonVoters ? 'Moving...' : `Move ${nonVoters.length} Non-Voters`}
                  </Button>
                </Col>
              </Row>
            )}
            {moveStatus && <Alert variant="info" className="mt-3">{moveStatus}</Alert>}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SessionPollAdmin;
